import type { RefObject, SyntheticEvent } from 'react';
import { createContext, useContext } from 'react';

import type { EncryptedPrivateKey } from '@sorare/wallet-shared';

import type { GoogleReCAPTCHA } from '#src/components/recaptcha/index.tsx';

export type SignedInInfo = {
  email: string;
  nickname: string;
  wallet: {
    ethereumAddress: string | null;
    solanaAddress: string | null;
  } | null;
  sorarePrivateKey: EncryptedPrivateKey | null;
  mustAcceptTcus: boolean;
  hasPassword: boolean;
  error?: 'invalid';
};
export type Prompt2faCallback = {
  resolve: (params: SignedInInfo) => void;
  reject: (reason: any) => void;
};

export type AcceptTermsInfo = {
  acceptTerms: boolean;
  acceptAgeLimit: boolean;
  acceptPrivacyPolicy: boolean;
  acceptGameRules: boolean;
  agreedToReceiveOffersFromPartners: boolean;
};

export type PromptTermsCallback = {
  resolve: (params: AcceptTermsInfo) => void;
  reject: (reason: any) => void;
};

export type PromptTermsOptions = {
  closable?: boolean;
  withoutAcceptTermsMutation?: boolean;
  tcuToken?: string | null;
  initialTermsDisplay?: boolean;
};

export type SignUpConfig = {
  step?: 0 | 1 | 2;
  nickname?: string;
} | null;

type ConnectionContextType = {
  signIn: () => void;
  signUp: (e?: SyntheticEvent, config?: SignUpConfig) => void;
  showMobileWebviewSignUpFlow: () => void;
  showMobilePasswordForgotten: () => void;
  setPasswordForgotten: (value: boolean) => void;
  toggleResetPassword: () => void;
  prompt2fa: (
    cb: Prompt2faCallback,
    otpSessionChallenge: string,
    reason?: string
  ) => void;
  promptTerms: (options: PromptTermsOptions, cb?: PromptTermsCallback) => void;
  promptNewDeviceConfirmation: () => void;
  passwordForgotten: boolean;
  acceptedTerms: AcceptTermsInfo | null;
  closeConnectionDialog: () => void;
  recaptchaRef: RefObject<GoogleReCAPTCHA | null>;
};

const modes = [
  'signin',
  'signup',
  'resetPassword',
  'forgotPassword',
  '2fa',
  'recoverKey',
  'restoreWallet',
  'addFundsEth',
  'addFunds',
  'newDevice',
  'showTerms',
  'acceptTerms',
  'verifyPhone',
  'kyc',
] as const;
export type Mode = (typeof modes)[number];
export type PopMode = 'mustAcceptTerms';

export const isConnectionMode = (action: Nullable<string>): action is Mode =>
  modes.includes(action as Mode);

export const ConnectionContext = createContext<ConnectionContextType | null>(
  null
);

export const useConnectionContext = () => useContext(ConnectionContext)!;
